import React, { useState, useEffect } from 'react';
import { X, FolderOpen, Plus, Trash2, Check, AlertCircle, CloudOff, Cloud, Edit2, RefreshCw } from 'lucide-react';
import { CloudStorageConfiguration, CloudStorageProvider } from '../types/storage';
import { getCloudStorageConfigs, saveCloudStorageConfig, deleteCloudStorageConfig, testCloudStorageConnection } from '../lib/cloudStorage';

interface CloudStorageConfigProps {
  onClose: () => void;
}

const availableProviders: CloudStorageProvider[] = [
  {
    id: 'azure',
    name: 'Azure Blob Storage',
    type: 'azure',
    icon: 'azure',
    description: 'Microsoft Azure Blob Storage für Unternehmen'
  },
  {
    id: 'onedrive',
    name: 'Microsoft OneDrive',
    type: 'onedrive',
    icon: 'onedrive',
    description: 'OneDrive Business oder Personal'
  },
  {
    id: 'custom',
    name: 'Benutzerdefiniert',
    type: 'custom',
    icon: 'cloud',
    description: 'Eigener Cloud-Speicher mit URL-Zugriff'
  }
];

export const CloudStorageConfig: React.FC<CloudStorageConfigProps> = ({ onClose }) => {
  const [configs, setConfigs] = useState<CloudStorageConfiguration[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState<CloudStorageConfiguration | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<CloudStorageProvider | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    baseUrl: '',
    folderPath: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = () => {
    const storedConfigs = getCloudStorageConfigs();
    setConfigs(storedConfigs);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      if (!selectedProvider) {
        throw new Error('Bitte wählen Sie einen Cloud-Anbieter aus');
      }

      if (!formData.name.trim()) {
        throw new Error('Bitte geben Sie einen Namen ein');
      }

      if (!formData.baseUrl.trim()) {
        throw new Error('Bitte geben Sie eine Base-URL ein');
      }

      if (!formData.folderPath.trim()) {
        throw new Error('Bitte geben Sie einen Ordnerpfad ein');
      }

      const newConfig: CloudStorageConfiguration = {
        id: editingConfig?.id || crypto.randomUUID(),
        name: formData.name,
        provider: selectedProvider.type,
        baseUrl: formData.baseUrl,
        folderPath: formData.folderPath,
        active: true,
        createdAt: editingConfig?.createdAt || new Date(),
        updatedAt: new Date()
      };

      await saveCloudStorageConfig(newConfig);
      loadConfigs();
      
      setSuccess('Cloud-Speicher erfolgreich konfiguriert');
      setShowAddForm(false);
      setEditingConfig(null);
      setSelectedProvider(null);
      setFormData({ name: '', baseUrl: '', folderPath: '' });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten');
    }
  };

  const handleEdit = (config: CloudStorageConfiguration) => {
    setEditingConfig(config);
    setSelectedProvider(availableProviders.find(p => p.type === config.provider) || null);
    setFormData({
      name: config.name,
      baseUrl: config.baseUrl,
      folderPath: config.folderPath
    });
    setShowAddForm(true);
  };

  const handleDelete = async (config: CloudStorageConfiguration) => {
    if (window.confirm('Möchten Sie diese Cloud-Speicher-Konfiguration wirklich löschen?')) {
      try {
        await deleteCloudStorageConfig(config.id);
        loadConfigs();
        setSuccess('Cloud-Speicher erfolgreich gelöscht');
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Fehler beim Löschen');
      }
    }
  };

  const handleTestConnection = async (config: CloudStorageConfiguration) => {
    try {
      setTestingConnection(config.id);
      const result = await testCloudStorageConnection(config);
      setSuccess(`Verbindung zu ${config.name} erfolgreich getestet`);
    } catch (error) {
      setError(`Verbindungstest fehlgeschlagen: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
    } finally {
      setTestingConnection(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FolderOpen className="w-6 h-6 text-blue-600" />
            <span>Cloud-Speicher konfigurieren</span>
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 text-red-700 p-4 rounded-lg flex items-start">
            <AlertCircle className="w-5 h-5 mt-0.5 mr-2 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-50 text-green-700 p-4 rounded-lg flex items-start">
            <Check className="w-5 h-5 mt-0.5 mr-2 flex-shrink-0" />
            <p>{success}</p>
          </div>
        )}

        {!showAddForm ? (
          <div className="space-y-6">
            {configs.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <CloudOff className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">Noch keine Cloud-Speicher konfiguriert</p>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-5 h-5" />
                  <span>Cloud-Speicher hinzufügen</span>
                </button>
              </div>
            ) : (
              <>
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus className="w-5 h-5" />
                    <span>Cloud-Speicher hinzufügen</span>
                  </button>
                </div>

                <div className="space-y-4">
                  {configs.map(config => (
                    <div 
                      key={config.id}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <Cloud className="w-5 h-5 text-blue-600" />
                            {config.name}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {availableProviders.find(p => p.type === config.provider)?.name}
                          </p>
                          <p className="text-sm text-gray-600 mt-2">
                            Ordnerpfad: {config.folderPath}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleTestConnection(config)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            disabled={!!testingConnection}
                            title="Verbindung testen"
                          >
                            {testingConnection === config.id ? (
                              <RefreshCw className="w-5 h-5 animate-spin" />
                            ) : (
                              <Cloud className="w-5 h-5" />
                            )}
                          </button>
                          <button
                            onClick={() => handleEdit(config)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Bearbeiten"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(config)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Löschen"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {!selectedProvider ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {availableProviders.map(provider => (
                  <button
                    key={provider.id}
                    type="button"
                    onClick={() => setSelectedProvider(provider)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all text-left"
                  >
                    <h3 className="font-semibold text-gray-900 mb-2">{provider.name}</h3>
                    <p className="text-sm text-gray-600">{provider.description}</p>
                  </button>
                ))}
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="z.B. Projekt A Cloud-Speicher"
                    className="w-full p-2.5 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Base-URL
                  </label>
                  <input
                    type="text"
                    value={formData.baseUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, baseUrl: e.target.value }))}
                    placeholder="https://example.com/storage"
                    className="w-full p-2.5 border border-gray-300 rounded-lg"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Die Basis-URL Ihres Cloud-Speichers
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ordnerpfad
                  </label>
                  <input
                    type="text"
                    value={formData.folderPath}
                    onChange={(e) => setFormData(prev => ({ ...prev, folderPath: e.target.value }))}
                    placeholder="/projekte/2025/fotos"
                    className="w-full p-2.5 border border-gray-300 rounded-lg"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Der Pfad im Cloud-Speicher, in dem die Fotos gespeichert werden sollen
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setSelectedProvider(null);
                      setEditingConfig(null);
                      setFormData({ name: '', baseUrl: '', folderPath: '' });
                    }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingConfig ? 'Aktualisieren' : 'Hinzufügen'}
                  </button>
                </div>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
};